// ForecastView.jsx
//
// Main forecast tab. Single-model view with:
//   - Time range pills (1D..1Y + month presets) - default 7D
//   - PTP / Payments toggle (default PTP)
//   - Model picker (single select)
//   - "▶ Run Forecast" button on the right
//
// Time range semantics on this page: symmetric window. "7D" = last 7
// days of actuals + next 7 days of predictions. The chart will eventually
// show both sides on one timeline with a divider at today.
//
// Data is empty until the forecast engine SP is built. The slicer chrome
// is wired and the model picker hits the live /forecast/models endpoint.

import React, { useEffect, useMemo, useState } from "react";
import {
  Alert, Box, CircularProgress, MenuItem, Select, Typography,
} from "@mui/material";

import { getForecastModels } from "../../services/forecast/forecastservices";
import {
  BLUE, GREEN, AMBER, PURPLE,
  RANGE_OPTIONS, getForecastDateWindow,
  PillGroup, ActionButton, ForecastKpiTile,
  SectionTitle, EmptyState,
  fmtDate,
} from "./forecastShared";


// ── PTP/Payments quick-toggle options ──────────────────────────────
// "PTP" lumps secured + uncommitted (both promise-flavored outcomes).
// "Payments" lumps same-day + 24hr (both payment-flavored outcomes).
// Default is PTP per spec.
const FILTER_OPTIONS = ["PTP", "Payments"];


export default function ForecastView() {
  const [rangePreset, setRangePreset]   = useState("7D");
  const [filterType,  setFilterType]    = useState("PTP");
  const [modelId,     setModelId]       = useState("");
  const [models,      setModels]        = useState([]);

  const [loadingModels, setLoadingModels] = useState(true);
  const [error,         setError]         = useState("");

  // Load models list to populate the picker. Sort active first, name asc.
  useEffect(() => {
    setLoadingModels(true);
    getForecastModels()
      .then(data => {
        const list = (data || []).filter(m => m.is_active);
        setModels(list);
        if (list.length > 0 && !modelId) {
          setModelId(list[0].id);
        }
      })
      .catch(e => {
        console.error("Failed to load models:", e);
        setError("Failed to load models");
      })
      .finally(() => setLoadingModels(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const window = useMemo(() => getForecastDateWindow(rangePreset), [rangePreset]);

  const handleRunForecast = () => {
    // Stubbed: will POST to /forecast/runs in a later slice.
    console.log("[stub] Run forecast", { modelId, rangePreset, filterType, window });
  };

  // Pull the selected model object for display
  const selectedModel = models.find(m => m.id === modelId);

  return (
    <Box sx={{ p: 2 }}>

      {/* ── Slicer header ── */}
      <Box sx={{
        display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-end",
        pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
      }}>

        {/* Model picker */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography sx={{ fontSize: 11, color: "text.secondary",
            textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Model
          </Typography>
          <Select
            value={modelId}
            onChange={e => setModelId(e.target.value)}
            size="small" variant="outlined"
            disabled={loadingModels || models.length === 0}
            displayEmpty
            sx={{
              fontSize: 12, borderRadius: "20px", minWidth: 180, height: 28,
              ".MuiOutlinedInput-notchedOutline": { borderColor: "divider", borderWidth: "0.5px" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BLUE },
              ".MuiSelect-select": { py: "3px", px: "10px", fontSize: 12, color: "text.secondary" },
              ".MuiSvgIcon-root": { fontSize: 16, color: "text.secondary" },
            }}
          >
            {models.length === 0 && (
              <MenuItem value="" disabled sx={{ fontSize: 12 }}>
                {loadingModels ? "Loading…" : "No models yet"}
              </MenuItem>
            )}
            {models.map(m => (
              <MenuItem key={m.id} value={m.id} sx={{ fontSize: 12 }}>
                {m.name}
              </MenuItem>
            ))}
          </Select>
        </Box>

        {/* PTP / Payments quick-toggle */}
        <PillGroup
          label="View"
          options={FILTER_OPTIONS}
          value={filterType}
          onChange={setFilterType}
        />

        {/* Date range */}
        <PillGroup
          label="Date range"
          options={RANGE_OPTIONS}
          value={rangePreset}
          onChange={setRangePreset}
          groups={[3, 4, 2, 4]}
        />

        {/* Right-aligned action */}
        <Box sx={{ ml: "auto", alignSelf: "flex-end" }}>
          <ActionButton onClick={handleRunForecast} icon="▶" color={GREEN}>
            Run Forecast
          </ActionButton>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loadingModels && (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 2 }}>
          <CircularProgress size={20} /> Loading forecast…
        </Box>
      )}

      {!loadingModels && models.length === 0 && (
        <EmptyState
          title="No models yet"
          hint="Go to the Models tab and create one to start forecasting."
        />
      )}

      {!loadingModels && selectedModel && (
        <>
          {/* Model summary line */}
          <Box sx={{ mb: 2, fontSize: 12, color: "text.secondary" }}>
            <Box component="span" sx={{ color: "text.primary", fontWeight: 500 }}>
              {selectedModel.name}
            </Box>
            {" · "}
            {selectedModel.persona_count} personas
            {selectedModel.description ? ` · ${selectedModel.description}` : ""}
          </Box>

          {/* Window summary */}
          <Box sx={{ mb: 2, fontSize: 11, color: "text.disabled" }}>
            Historical: <Box component="span" sx={{ color: "text.secondary" }}>
              {fmtDate(window.historicalStart)} → {fmtDate(window.historicalEnd)}
            </Box>
            {"  ·  "}
            Forecast: <Box component="span" sx={{ color: "text.secondary" }}>
              {fmtDate(window.forecastStart)} → {fmtDate(window.forecastEnd)}
            </Box>
          </Box>

          {/* ── Headline KPIs (placeholders until SP exists) ── */}
          <SectionTitle>Predicted {filterType.toLowerCase()}</SectionTitle>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))",
            gap: 1, mb: 1.5 }}>
            <ForecastKpiTile
              label="Predicted $"
              value="—"
              sublabel="No forecast run yet"
              color={AMBER}
            />
            <ForecastKpiTile
              label="Predicted contacts"
              value="—"
              sublabel="—"
              color={BLUE}
            />
            <ForecastKpiTile
              label="Predicted connects"
              value="—"
              sublabel="—"
              color={GREEN}
            />
            <ForecastKpiTile
              label="Predicted successes"
              value="—"
              sublabel="—"
              color={PURPLE}
            />
          </Box>

          {/* ── Chart area (empty state until forecast data exists) ── */}
          <SectionTitle>Historical actuals + forecast over time</SectionTitle>
          <EmptyState
            title="No forecast data yet"
            hint='Click "Run Forecast" to generate predictions for this model and window. The forecast engine SP will be built in a later slice.'
          />

          {/* ── Future sections (commented placeholders for next slices) ── */}
          {/*
              SectionTitle: Breakdown by channel persona
                - one row per channel_persona showing predicted contacts/$/etc
              SectionTitle: Breakdown by DPD bucket
                - one row per DPD bucket
              SectionTitle: Actuals vs forecast (when historical actuals exist)
                - line chart with actuals (solid) and prediction (dashed)
                  meeting at today's date
          */}
        </>
      )}
    </Box>
  );
}